import {CookiePolicyPage} from './common/consent/cookie-policy.page';
import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {LoginComponent} from './login/login.component';
import {AuthGuardService} from './common/services/auth-guard.service';
import {NotFoundPage} from './core/pages/not-found.page';
import {UnathorizedPage} from './core/pages/403-unauthorized.page';
import {ImpersonatePage} from './core/pages/impersonate.page';
import {SolarSystemPage} from './core/pages/solar-system.page';
import {CanvasAuthGuardService} from './common/services/canvas-auth-guard.service';
import {LoginGuardService} from './common/services/login-guard.service';
import {IframeApiGuardService} from './common/services/iframe-api-guard.service';
import {EmptyComponent} from './common/components/empty.component';
import {AngularFireAuthGuard, customClaims, redirectLoggedInTo, redirectUnauthorizedTo} from '@angular/fire/compat/auth-guard';
import {pipe} from 'rxjs';
import {map} from 'rxjs/operators';
import {SubscriptionPlanType} from './common/enums/subscription-plan-type';
import {MapLoaderGuardService} from './common/services/map-loader-guard.service';
import {CustomTokenSigninGuardService} from './common/services/custom-token-signin-guard.service';
import {SignoutComponent} from './common/components/signout.component';
import {NewProjectGuardService} from './common/services/new-project-guard.service';
import {GuestStartPage} from './core/pages/guest-start.page';
import {EmbedUnavailablePage} from './core/pages/embed-unavailable.page';
import {EmailRevertPage} from './core/pages/email-revert.page';
import {UnsubscribePage} from './core/pages/unsubscribe.page';

const redirectUnauthorizedToLogin = () => redirectUnauthorizedTo(['login', '?rdt=app/p/fkdsanjf1234']);
const redirectLoggedInToApp = () => redirectLoggedInTo(['app']);
const redirectLoggedInToDashboard = () => redirectLoggedInTo(['dashboard']);

const noOrganisationMember = () =>
  pipe(customClaims, map((claims: any) => (claims[`subscriptionPlanType`] !== SubscriptionPlanType.ENTERPRISE_USER && !claims[`subscriptionManagedBy`]) || ['dashboard']));

const routes: Routes = [
  // {
  //   path: 'app/p/:pid',
  //   loadChildren: () => import('./canvas/canvas.module').then(m => m.CanvasModule),
  //   canActivate: [AuthGuardService],
  //   // children: [
  //   //   {
  //   //     path: 'point/:pointId',
  //   //     loadChildren: () => import('./canvas/canvas.module').then(m => m.CanvasModule)
  //   //   },
  //   //   {
  //   //     path: 'polygon/:polygonId',
  //   //     loadChildren: () => import('./canvas/canvas.module').then(m => m.CanvasModule)
  //   //   },
  //   //   {
  //   //     path: 'spot/:spotId',
  //   //     loadChildren: () => import('./canvas/canvas.module').then(m => m.CanvasModule)
  //   //   },
  //   //   {
  //   //     path: '',
  //   //     loadChildren: () => import('./canvas/canvas.module').then(m => m.CanvasModule)
  //   //   },
  //   // ]
  // },
  // {
  //   path: 'app/:pid',
  //   redirectTo: 'app/p/:pid',
  // },
  {
    path: 'iframe_api',
    component: EmptyComponent,
    canActivate: [MapLoaderGuardService, IframeApiGuardService],
  },
  // An Open Guest owner's public link. Deliberately short -- it gets printed,
  // pasted into website builders and read out over the phone -- and outside
  // /app, whose guards expect a signed-in or token-carrying visitor.
  {
    path: 'g/:linkId',
    component: GuestStartPage,
  },
  // Where the integrator's token flow lands when it cannot start a project --
  // it used to leave the visitor on a blank page.
  {
    // The cookie policy: public, so anyone can read it before signing in.
    path: 'cookies',
    component: CookiePolicyPage,
  },
  {
    path: 'guest/unavailable',
    component: GuestStartPage,
  },
  {
    // An embedded project (iframe API) that cannot be shown, and why.
    path: 'embed/unavailable',
    component: EmbedUnavailablePage,
  },
  // "This wasn't me" from the email-change notice. Public: whoever changed the
  // address may also have changed the password.
  {
    path: 'account/email-revert',
    component: EmailRevertPage,
  },
  // "Unsubscribe" at the foot of an outreach email (Admin -> Growth). Public:
  // the person reading it has no account.
  {
    path: 'unsubscribe/:token',
    component: UnsubscribePage,
  },
  {
    path: 'app/projects/guest/new',
    component: EmptyComponent,
    canActivate: [NewProjectGuardService]
  },
  {
    path: 'app',
    canActivate: [CustomTokenSigninGuardService],
    children: [
      {
        path: '',
        canActivate: [MapLoaderGuardService],
        children: [
          {
            path: '',
            canActivate: [CanvasAuthGuardService],
            loadChildren: () => import('./canvas/canvas.module').then(m => m.CanvasModule),
          }
        ]
      }
    ]
  },
  {
    path: 'dashboard',
    canActivate: [CustomTokenSigninGuardService],
    children: [
      {
        path: '',
        canActivate: [MapLoaderGuardService],
        children: [
          {
            path: '',
            canActivate: [AuthGuardService],
            loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardModule),
          }
        ]
      }
    ]
  },
  {
    path: 'billing',
    loadChildren: () => import('./billing/billing.module').then(m => m.BillingModule),
    // An organisation member's plan is the organisation's: the server refuses
    // their checkout, so they were shown one that could only fail.
    canActivate: [AuthGuardService, AngularFireAuthGuard],
    data: {authGuardPipe: noOrganisationMember}
  },
  {
    path: 'login',
    canActivate: [CustomTokenSigninGuardService],
    children: [
      {
        path: '',
        canActivate: [LoginGuardService],
        component: LoginComponent
      }
    ]
  },
  {
    path: 'signout',
    component: SignoutComponent
  },
  {
    // A partner's invitation email links here; it is answered signed in.
    path: 'partner-invite/:token',
    redirectTo: '/dashboard/partner-invite/:token'
  },
  {
    path: 'session/expired',
    component: NotFoundPage
  },
  {
    path: 'projectNotFound',
    component: NotFoundPage
  },
  {
    path: '404',
    component: NotFoundPage
  },
  {
    path: '403',
    redirectTo: '/unauthorized'
  },
  {
    // The admin console's "View as user" lands here (no guard: it signs in).
    path: 'impersonate',
    component: ImpersonatePage
  },
  {
    path: 'unauthorized',
    component: UnathorizedPage
  },
  {
    path: 'solar-system',
    component: SolarSystemPage
  },
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full'
  },
  {
    path: '**',
    redirectTo: '404'
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
